import * as React from "react";
import { useEffect, useState, useContext } from "react";
import { useHistory, useParams } from "react-router-dom";
import { NotificationAlertContext } from "contexts/notificationAlertContext";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import ReactTable from "components/ReactTable/ReactTableWithDynamicPagination.js";

// react-bootstrap components
import {
  Card,
  Container,
  Row,
  Col,
  Button,
  Spinner,
  Table,
  Form,
} from "react-bootstrap";
import {
  getClientEnvironmentEntitlementsAvailable,
  getChannelEntitlementUsage,
  updateChannelEntitlementUsage,
} from "graphql/queries.js";
import _ from "lodash";
import { API, graphqlOperation } from "aws-amplify";
import { AuthContext } from "contexts/authContext";
import { tourGuideSteps } from "views/ManageLicensing/Entitlements/EnvironmentEntitlementsSteps";
import { useTour } from "@reactour/tour";

const actionCustomButton = {
  width: "90%",
};

// Checkbox styles for better UI
const checkboxContainerStyle = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "8px 0",
};

const checkboxStyle = {
  width: "20px",
  height: "20px",
  cursor: "pointer",
};

// Group header checkbox style - same as regular checkboxes
const groupCheckboxStyle = {
  width: "20px",
  height: "20px",
  cursor: "pointer",
  marginLeft: "57px",
};

// Group header row style
const groupHeaderStyle = {
  display: "flex",
  alignItems: "center",
  paddingTop: "8px",
  paddingBottom: "8px",
  paddingLeft: "12px",
  paddingRight: "12px",
  cursor: "pointer",
  marginLeft: "-12px",
  marginRight: "-12px",
  fontWeight: "bold",
};

// Chevron icon style
const chevronStyle = {
  marginRight: "10px",
  width: "12px",
};

// Cell padding style
const cellPaddingStyle = {
  padding: "8px 0",
  display: "block",
};

// Channel name cell style (indented)
const channelNameCellStyle = {
  paddingTop: "8px",
  paddingBottom: "8px",
  paddingLeft: "22px",
  paddingRight: "0",
  display: "block",
};

// Select all label style
const selectAllLabelStyle = {
  marginLeft: "6px",
  cursor: "pointer",
};

function EnvironmentEntitlements() {
  // Hooks
  const notify = useContext(NotificationAlertContext);
  const currentUser = useContext(AuthContext);
  const history = useHistory();

  const { clientId, clientEnvironmentId } = useParams();
  const [environmentData, setEnvironmentData] = useState(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);

  const [channelEntitlementUsageData, setChannelEntitlementUsageData] =
    useState([]);

  const [channelReportingEntitlementId, setChannelReportingEntitlementId] =
    useState(null);
  const [channelArchivingEntitlementId, setChannelArchivingEntitlementId] =
    useState(null);

  // Tour Guide
  const { setIsOpen, setCurrentStep, setSteps } = useTour();

  const [channelReportingAllotment, setChannelReportingAllotment] = useState(0);
  const [channelArchivingAllotment, setChannelArchivingAllotment] = useState(0);

  const [channelReportingUsed, setChannelReportingUsed] = useState(0);
  const [channelArchivingUsed, setChannelArchivingUsed] = useState(0);

  const [channelReportingAdd, setChannelReportingAdd] = useState(0);
  const [channelArchivingAdd, setChannelArchivingAdd] = useState(0);

  const [channelReportingRemove, setChannelReportingRemove] = useState(0);
  const [channelArchivingRemove, setChannelArchivingRemove] = useState(0);

  const [channelReportingSelected, setChannelReportingSelected] = useState([]);
  const [channelArchivingSelected, setChannelArchivingSelected] = useState([]);

  const [valueChanged, setValueChanged] = useState(false);
  const [parameters, setParameters] = useState(null);

  const [showLoader, setShowLoader] = useState(false);
  const [showError, setShowError] = useState(false);
  const groupName = currentUser.groupName;

  const [refreshKey, setRefreshKey] = useState(0);
  
  // Pagination state
  const [currentOffset, setCurrentOffset] = useState(0);
  const [currentLimit, setCurrentLimit] = useState(25);
  
  // State to track which groups are collapsed
  const [collapsedGroups, setCollapsedGroups] = useState({});

  // Toggle group collapse state
  const toggleGroupCollapse = (groupName) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  // Expand all groups
  const expandAllGroups = () => {
    setCollapsedGroups({});
  };

  // Collapse all groups
  const collapseAllGroups = () => {
    const allCollapsed = {};
    getUniqueGroups().forEach((group) => {
      allCollapsed[group] = true;
    });
    setCollapsedGroups(allCollapsed);
  };

  // Get unique groups from data
  const getUniqueGroups = () => {
    const groups = new Set();
    channelEntitlementUsageData.forEach((channel) => {
      groups.add(channel.channel_group || "Ungrouped");
    });
    return Array.from(groups).sort((a, b) => {
      if (a === "Ungrouped") return 1;
      if (b === "Ungrouped") return -1;
      return a.localeCompare(b);
    });
  };

  // Get channels for a specific group
  const getChannelsForGroup = (groupName) => {
    return channelEntitlementUsageData.filter(
      (channel) => (channel.channel_group || "Ungrouped") === groupName
    );
  };

  // Used to determine the state of the group-level "Select All" checkbox for monitoring
  // When all channels in a group are selected → checkbox shows as checked
  // When some (but not all) are selected → checkbox shows as indeterminate (via isGroupSomeReportingSelected)
  // When none are selected → checkbox shows as unchecked
  // Check if all channels in a group are selected for reporting
  const isGroupAllReportingSelected = (groupName) => {
    const channels = getChannelsForGroup(groupName);
    return channels.length > 0 && channels.every((ch) => 
      channelReportingSelected.includes(ch.channel_id)
    );
  };

  // Check if some (but not all) channels in a group are selected for reporting
  const isGroupSomeReportingSelected = (groupName) => {
    const channels = getChannelsForGroup(groupName);
    const selectedCount = channels.filter((ch) => 
      channelReportingSelected.includes(ch.channel_id)
    ).length;
    return selectedCount > 0 && selectedCount < channels.length;
  };

  // Check if all channels in a group are selected for archiving
  const isGroupAllArchivingSelected = (groupName) => {
    const channels = getChannelsForGroup(groupName);
    return channels.length > 0 && channels.every((ch) => 
      channelArchivingSelected.includes(ch.channel_id)
    );
  };

  // Check if some (but not all) channels in a group are selected for archiving
  const isGroupSomeArchivingSelected = (groupName) => {
    const channels = getChannelsForGroup(groupName);
    const selectedCount = channels.filter((ch) => 
      channelArchivingSelected.includes(ch.channel_id)
    ).length;
    return selectedCount > 0 && selectedCount < channels.length;
  };

  // Toggle all reporting selections for a group
  const toggleGroupReporting = (groupName) => {
    setValueChanged(true);
    const channels = getChannelsForGroup(groupName);
    const channelIds = channels.map((ch) => ch.channel_id);
    
    if (isGroupAllReportingSelected(groupName)) {
      setChannelReportingSelected((prev) => 
        prev.filter((id) => !channelIds.includes(id))
      );
    } else {
      setChannelReportingSelected((prev) => {
        const newSet = new Set([...prev, ...channelIds]);
        return Array.from(newSet);
      });
    }
  };

  // Toggle all archiving selections for a group
  const toggleGroupArchiving = (groupName) => {
    setValueChanged(true);
    const channels = getChannelsForGroup(groupName);
    const channelIds = channels.map((ch) => ch.channel_id);
    
    if (isGroupAllArchivingSelected(groupName)) {
      setChannelArchivingSelected((prev) => 
        prev.filter((id) => !channelIds.includes(id))
      );
    } else {
      setChannelArchivingSelected((prev) => {
        const newSet = new Set([...prev, ...channelIds]);
        return Array.from(newSet);
      });
    }
  };


  // Table configuration for static pagination
  const numberOfRowsData = [25, 50, 100];
  const columnMapping = {
    "Channel Group": "channel_group",
    "Channel Name": "channel_name",
  };

  // State to track independent sort for groups and channels
  // active: false means sort hasn't been triggered by user yet (use natural order)
  const [groupSort, setGroupSort] = useState({ direction: "ASC", active: false });
  const [channelSort, setChannelSort] = useState({ direction: "ASC", active: false });
  const [activeSortColumn, setActiveSortColumn] = useState("channel_group");
  
  // Ref to store pending sort direction (ascOrDesc comes BEFORE sort in ReactTable)
  const pendingSortDirection = React.useRef(null);

  // Custom setStateForPagnination to handle sorting and pagination from ReactTable
  const setStateForPagnination = (key, value) => {
    if (key === "ascOrDesc") {
      // Store the direction - will be applied when "sort" is called
      pendingSortDirection.current = value;
    } else if (key === "sort") {
      const columnName = columnMapping[value] || value;
      setActiveSortColumn(columnName);
      
      // Apply the pending sort direction to the correct column
      const direction = pendingSortDirection.current || "ASC";
      if (columnName === "channel_group") {
        setGroupSort({ direction, active: true });
      } else if (columnName === "channel_name") {
        setChannelSort({ direction, active: true });
      }
      pendingSortDirection.current = null;
    } else if (key === "offset") {
      // ReactTable passes page number (0, 1, 2...)
      setCurrentOffset(value);
    } else if (key === "limit" || key === "currentLimit") {
      setCurrentLimit(value);
      setCurrentOffset(0); // Reset to first page when limit changes
    }
  };

  // Transform data for ReactTable with group headers
  const getTableData = () => {
    const groups = getUniqueGroups();
    const tableData = [];

    // Sort groups only if user has explicitly clicked on group sort
    let sortedGroups = [...groups];
    if (groupSort.active) {
      sortedGroups.sort((a, b) => {
        // Keep "Ungrouped" at the end regardless of sort direction
        if (a === "Ungrouped") return 1;
        if (b === "Ungrouped") return -1;
        const comparison = a.localeCompare(b);
        return groupSort.direction === "ASC" ? comparison : -comparison;
      });
    }

    sortedGroups.forEach((groupName) => {
      // Sort channels within group only if user has explicitly clicked on channel sort
      let channels = [...getChannelsForGroup(groupName)];
      if (channelSort.active) {
        channels.sort((a, b) => {
          const comparison = (a.channel_name || "").localeCompare(b.channel_name || "");
          return channelSort.direction === "ASC" ? comparison : -comparison;
        });
      }
      const isCollapsed = collapsedGroups[groupName];
      const allReportingSelected = isGroupAllReportingSelected(groupName);
      const someReportingSelected = isGroupSomeReportingSelected(groupName);
      const allArchivingSelected = isGroupAllArchivingSelected(groupName);
      const someArchivingSelected = isGroupSomeArchivingSelected(groupName);

      // Add group header row
      tableData.push({
        channel_id: `group-${groupName}`,
        isGroupHeader: true,
        channel_group: (
          <div
            style={groupHeaderStyle}
            onClick={() => toggleGroupCollapse(groupName)}
          >
            <i
              className={`fas fa-chevron-${isCollapsed ? "right" : "down"}`}
              style={chevronStyle}
            ></i>
            <span>{groupName} ({channels.length} channels)</span>
          </div>
        ),
        channel_name: (
          <span style={cellPaddingStyle}>
            &nbsp;
          </span>
        ),
        monitoring_enabled: (
          <div style={checkboxContainerStyle}>
            <input
              key={`reporting-group-${groupName}-${allReportingSelected}-${someReportingSelected}`}
              type="checkbox"
              checked={allReportingSelected}
              ref={(el) => {
                if (el) el.indeterminate = someReportingSelected;
              }}
              style={groupCheckboxStyle}
              onClick={(e) => e.stopPropagation()}
              onChange={() => toggleGroupReporting(groupName)}
              title={allReportingSelected ? "Deselect all monitoring in group" : "Select all monitoring in group"}
            />
            <span 
              style={selectAllLabelStyle}
              onClick={(e) => { e.stopPropagation(); toggleGroupReporting(groupName); }}
            >
              <small>Select All</small>
            </span>
          </div>
        ),
        archiving_enabled: (
          <div style={checkboxContainerStyle}>
            <input
              key={`archiving-group-${groupName}-${allArchivingSelected}-${someArchivingSelected}`}
              type="checkbox"
              checked={allArchivingSelected}
              ref={(el) => {
                if (el) el.indeterminate = someArchivingSelected;
              }}
              style={groupCheckboxStyle}
              onClick={(e) => e.stopPropagation()}
              onChange={() => toggleGroupArchiving(groupName)}
              title={allArchivingSelected ? "Deselect all archiving in group" : "Select all archiving in group"}
            />
            <span 
              style={selectAllLabelStyle}
              onClick={(e) => { e.stopPropagation(); toggleGroupArchiving(groupName); }}
            >
              <small>Select All</small>
            </span>
          </div>
        ),
      });

      // Add channel rows if group is not collapsed
      if (!isCollapsed) {
        channels.forEach((channel) => {
          tableData.push({
            channel_id: channel.channel_id,
            channel_group: (
              <span style={cellPaddingStyle}>
                {channel.channel_group || "-"}
              </span>
            ),
            channel_name: (
              <span style={channelNameCellStyle}>
                {channel.channel_name}
              </span>
            ),
            monitoring_enabled: (
              <div style={checkboxContainerStyle}>
                <input
                  type="checkbox"
                  id={`reporting-${channel.channel_id}`}
                  checked={channelReportingSelected.includes(channel.channel_id)}
                  style={checkboxStyle}
                  onChange={(event) => {
                    setValueChanged(true);
                    if (event.target.checked) {
                      setChannelReportingSelected([
                        ...channelReportingSelected,
                        channel.channel_id,
                      ]);
                    } else {
                      setChannelReportingSelected(
                        channelReportingSelected.filter(
                          (item) => item !== channel.channel_id
                        )
                      );
                    }
                  }}
                />
              </div>
            ),
            archiving_enabled: (
              <div style={checkboxContainerStyle}>
                <input
                  type="checkbox"
                  id={`archiving-${channel.channel_id}`}
                  checked={channelArchivingSelected.includes(channel.channel_id)}
                  style={checkboxStyle}
                  onChange={(event) => {
                    setValueChanged(true);
                    if (event.target.checked) {
                      setChannelArchivingSelected((prev) => [
                        ...prev,
                        channel.channel_id,
                      ]);
                    } else {
                      setChannelArchivingSelected(
                        channelArchivingSelected.filter(
                          (item) => item !== channel.channel_id
                        )
                      );
                    }
                  }}
                />
              </div>
            ),
          });
        });
      }
    });

    // Apply pagination - currentOffset is page number, calculate actual slice offset
    const actualOffset = currentOffset * currentLimit;
    return tableData.slice(actualOffset, actualOffset + currentLimit);
  };
  
  // Get total count for pagination (full table data without slicing)
  const getTotalTableRowCount = () => {
    const groups = getUniqueGroups();
    let totalRows = 0;
    
    groups.forEach((groupName) => {
      // Count group header row
      totalRows += 1;
      // Count channel rows if group is not collapsed
      if (!collapsedGroups[groupName]) {
        totalRows += getChannelsForGroup(groupName).length;
      }
    });
    
    return totalRows;
  };

  // Column definitions for ReactTable
  const getColumns = () => [
    {
      Header: "Channel Group",
      accessor: "channel_group",
      filterable: false,
      sortable: true,
      minWidth: 150,
    },
    {
      Header: "Channel Name",
      accessor: "channel_name",
      filterable: false,
      sortable: true,
      minWidth: 200,
    },
    {
      Header: () => (
        <div style={{ textAlign: "center" }}>
            Monitoring Enabled
          <br />
            <Button
              className="btn-fill"
              size="sm"
              variant="primary"
              onClick={(e) => {
                e.stopPropagation();
                selectAllReporting();
              }}
            >
              Select All
            </Button>
          &nbsp;
            <Button
              className="btn-fill"
              size="sm"
            variant="btn-outline"
              onClick={(e) => {
                e.stopPropagation();
                unselectAllReporting();
              }}
            >
              Unselect All
            </Button>
        </div>
      ),
      accessor: "monitoring_enabled",
      filterable: false,
      sortable: false,
      minWidth: 200,
    },
    {
      Header: () => (
        <div style={{ textAlign: "center" }}>
            Archiving Enabled
          <br />
            <Button
              className="btn-fill"
              size="sm"
              variant="primary"
              onClick={(e) => {
                e.stopPropagation();
                selectAllArchiving();
              }}
            >
              Select All
            </Button>
          &nbsp;
            <Button
              className="btn-fill"
              size="sm"
            variant="btn-outline"
              onClick={(e) => {
                e.stopPropagation();
                unselectAllArchiving();
              }}
            >
              Unselect All
            </Button>
        </div>
      ),
      accessor: "archiving_enabled",
      filterable: false,
      sortable: false,
      minWidth: 200,
    },
  ];

  const refreshPage = () => {
    setRefreshKey(refreshKey + 1);
    setChannelReportingAdd(0);
    setChannelArchivingAdd(0);
    setChannelReportingRemove(0);
    setChannelArchivingRemove(0);
    setChannelReportingSelected([]);
    setChannelArchivingSelected([]);
    setValueChanged(false);
    setParameters(null);
    setCollapsedGroups({});
  };
  const fetchClientEnvironmentEntitlementsAvailable = async () => {
    try {
      const params = {
        client_environment_id: clientEnvironmentId,
      };
      const response = await API.graphql(
        graphqlOperation(getClientEnvironmentEntitlementsAvailable, params)
      );
      if (response.data?.getClientEnvironmentEntitlementsAvailable?.success) {
        const environmentData = _.get(
          response,
          "data.getClientEnvironmentEntitlementsAvailable.body",
          []
        );
        const entitlements_available = _.get(
          environmentData,
          "entitlements_available",
          []
        );
        for (let index = 0; index < entitlements_available.length; index++) {
          if (entitlements_available[index].type == "CHANNEL_METRICS") {
            setChannelReportingEntitlementId(
              entitlements_available[index].entitlement_id
            );
            setChannelReportingAllotment(
              entitlements_available[index].allotment
            );
            setChannelReportingUsed(entitlements_available[index].used);
          } else if (
            entitlements_available[index].type == "CHANNEL_ARCHIVING"
          ) {
            setChannelArchivingEntitlementId(
              entitlements_available[index].entitlement_id
            );
            setChannelArchivingAllotment(
              entitlements_available[index].allotment
            );
            setChannelArchivingUsed(entitlements_available[index].used);
          }
        }
        setEnvironmentData(environmentData);
      } else {
        console.error(
          response.data?.getClientEnvironmentEntitlementsAvailable?.message
        );
      }
      return response.data?.getClientEnvironmentEntitlementsAvailable?.success;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const fetchChannelEntitlementUsage = async () => {
    try {
      const params = {
        client_environment_id: clientEnvironmentId,
      };
      const response = await API.graphql(
        graphqlOperation(getChannelEntitlementUsage, params)
      );
      if (response.data?.getChannelEntitlementUsage?.success) {
        const responseChannelEntitlementUsageData = _.get(
          response,
          "data.getChannelEntitlementUsage.body",
          []
        );
        const new_entitlement_data = [];
        responseChannelEntitlementUsageData.map((channel_entitlement) => {
          const channel = {
            channel_id: channel_entitlement.channel_id,
            channel_name: channel_entitlement.channel_name,
            channel_group: channel_entitlement.channel_group,
            entitlement_types: channel_entitlement.entitlement_types,
          };
          new_entitlement_data.push(channel);
          channel_entitlement.entitlement_types.map((type) => {
            if (type == "CHANNEL_METRICS") {
              setChannelReportingSelected((channelReportingSelected) => [
                ...channelReportingSelected,
                channel_entitlement.channel_id,
              ]);
            } else if (type == "CHANNEL_ARCHIVING") {
              setChannelArchivingSelected((channelArchivingSelected) => [
                ...channelArchivingSelected,
                channel_entitlement.channel_id,
              ]);
            }
          });
        });
        setChannelEntitlementUsageData(new_entitlement_data);
      } else {
        console.error(response.data?.getChannelEntitlementUsage?.message);
      }
      return response.data?.getChannelEntitlementUsage?.success;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const selectAllReporting = () => {
    setValueChanged(true);
    setChannelReportingSelected(
      channelEntitlementUsageData.map((channel) => channel.channel_id)
    );
  };

  const selectAllArchiving = () => {
    setValueChanged(true);
    setChannelArchivingSelected(
      channelEntitlementUsageData.map((channel) => channel.channel_id)
    );
  };

  const unselectAllReporting = () => {
    setValueChanged(true);
    setChannelReportingSelected([]);
  };

  const unselectAllArchiving = () => {
    setValueChanged(true);
    setChannelArchivingSelected([]);
  };

  const buildParameters = async () => {
    let channel_reporting_delete = [];
    let channel_archiving_delete = [];
    let delete_list = [];
    let add_list = [];

    let num_channel_reporting_add = 0;
    let num_channel_archiving_add = 0;

    channelEntitlementUsageData.map((channel) => {
      // check for add
      if (
        channelReportingSelected.includes(channel.channel_id) &&
        !channel.entitlement_types.includes("CHANNEL_METRICS")
      ) {
        let add_obj = {};
        add_obj["entitlement_id"] = channelReportingEntitlementId;
        add_obj["entitlement_type"] = "CHANNEL_METRICS";
        add_obj["channel_id"] = channel.channel_id;
        add_list.push(add_obj);
        num_channel_reporting_add++;
      }
      if (
        channelArchivingSelected.includes(channel.channel_id) &&
        !channel.entitlement_types.includes("CHANNEL_ARCHIVING")
      ) {
        let add_obj = {};
        add_obj["entitlement_id"] = channelArchivingEntitlementId;
        add_obj["entitlement_type"] = "CHANNEL_ARCHIVING";
        add_obj["channel_id"] = channel.channel_id;
        add_list.push(add_obj);
        num_channel_archiving_add++;
      }

      // check for delete
      channel.entitlement_types.map((type) => {
        if (
          type == "CHANNEL_METRICS" &&
          !channelReportingSelected.includes(channel.channel_id)
        ) {
          channel_reporting_delete.push(channel.channel_id);
        }
        if (
          type == "CHANNEL_ARCHIVING" &&
          !channelArchivingSelected.includes(channel.channel_id)
        ) {
          channel_archiving_delete.push(channel.channel_id);
        }
      });
    });

    if (channel_reporting_delete.length > 0) {
      let delete_obj = {};
      delete_obj["entitlement_id"] = channelReportingEntitlementId;
      delete_obj["entitlement_type"] = "CHANNEL_METRICS";
      delete_obj["channel_list"] = channel_reporting_delete;
      delete_list.push(delete_obj);
    }

    if (channel_archiving_delete.length > 0) {
      let delete_obj = {};
      delete_obj["entitlement_id"] = channelArchivingEntitlementId;
      delete_obj["entitlement_type"] = "CHANNEL_ARCHIVING";
      delete_obj["channel_list"] = channel_archiving_delete;
      delete_list.push(delete_obj);
    }

    let params = {
      client_environment_id: clientEnvironmentId,
      delete_list: JSON.stringify(delete_list),
      add_list: JSON.stringify(add_list),
    };

    setParameters(params);
    setChannelReportingAdd(num_channel_reporting_add);
    setChannelArchivingAdd(num_channel_archiving_add);
    setChannelReportingRemove(channel_reporting_delete.length);
    setChannelArchivingRemove(channel_archiving_delete.length);
  };

  const handleSubmit = async () => {
    try {
      notify("info", "Sending entitlement update request.");

      const response = await API.graphql(
        graphqlOperation(updateChannelEntitlementUsage, parameters)
      );

      if (response.data?.updateChannelEntitlementUsage?.success) {
        notify("success");
        refreshPage();
      } else {
        notify("danger", response.data?.updateChannelEntitlementUsage?.message);
        console.error(response.data?.updateChannelEntitlementUsage?.message);
      }
      return response.data?.updateChannelEntitlementUsage?.success;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  useEffect(() => {
    async function fetchData() {
      setShowLoader(true);
      const promiseAllResults = await Promise.all([
        fetchClientEnvironmentEntitlementsAvailable(),
        fetchChannelEntitlementUsage(),
      ]);
      const isSuccess = promiseAllResults.every((value) => value);
      if (!isSuccess) {
        notify("danger");
        groupName === "internal" ? history.goBack() : setShowError(true);
      }
      setShowLoader(false);
    }
    fetchData();
  }, [refreshKey]);

  if (showLoader) return <Spinner animation="grow" />;

  if (showError)
    return `Could not load the page at this moment. Please try again later.`;

  return (
    <>
      {alert}
      <Container fluid>
        <Row>
          <Col>
            <h2>Environment Entitlements</h2>
          </Col>
          {currentUser.groupName === "external" && (
            <Col className="my-auto text-right">
              <Button
                variant="secondary"
                className="btn-outline"
                onClick={() => {
                  setSteps(tourGuideSteps);
                  setCurrentStep(0);
                  setIsOpen(true);
                }}
              >
                <i className="fas fa-desktop"></i> View Our Tour Guide
              </Button>
            </Col>
          )}
        </Row>
        <Row>
          <Col md="12" id="entitlement-header-1">
            <h5>Client Name: {environmentData?.client_name}</h5>
            <h5>
              Client Environment Name:{" "}
              {environmentData?.client_environment_name}
            </h5>
            <h5>
              Client Environment Id: {environmentData?.client_environment_id}
            </h5>
          </Col>
          <Col md="12" id="entitlement-header-2">
            <h5>Monitoring Entitlements:</h5>
            <dt>&nbsp;&nbsp;Client Allotment: {channelReportingAllotment}</dt>
            <dt>&nbsp;&nbsp;Client Channels Enabled: {channelReportingUsed}</dt>
            <br></br>
            <h5>Archiving Entitlements:</h5>
            <dt>&nbsp;&nbsp;Client Allotment: {channelArchivingAllotment} GB</dt>
            <dt>&nbsp;&nbsp;Client Channels Enabled: {channelArchivingUsed}</dt>
          </Col>
        </Row>
        <hr />
        <Col md="12" id="entitlement-data-grid">
          <Card>
            <Card.Header>
              <Row
                style={{ textAlign: "center" }}
                id="entitlement-data-grid-buttons"
              >
                <Col md="3">
                </Col>
                <Col md="3">
                  <Button
                    style={actionCustomButton}
                    variant="default"
                    className="btn-outline"
                    onClick={refreshPage}
                  >
                    Reset
                  </Button>
                </Col>
                <Col md="3">
                  <Button
                    style={actionCustomButton}
                    variant="primary"
                    disabled={!valueChanged}
                    onClick={() => {
                      buildParameters();
                      setShowSubmitModal(true);
                    }}
                  >
                    Save Changes
                  </Button>
                </Col>
                <Col md="3">
                </Col>
              </Row>
              <Row style={{ marginTop: "10px" }}>
                <Col md="12" style={{ textAlign: "left" }}>
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={expandAllGroups}
                    style={{ marginRight: "8px" }}
                  >
                    <i className="fas fa-expand-alt"></i> Expand All
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={collapseAllGroups}
                  >
                    <i className="fas fa-compress-alt"></i> Collapse All
                  </Button>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body>
              <ReactTable
                data={getTableData()}
                columns={getColumns()}
                totalCount={getTotalTableRowCount()}
                numberOfRowsData={numberOfRowsData}
                currentLimit={currentLimit}
                currentOffset={currentOffset}
                ascOrDescValue={activeSortColumn === "channel_group" ? groupSort.direction : channelSort.direction}
                sortValue={activeSortColumn === "channel_group" ? "Channel Group" : "Channel Name"}
                columnMapping={columnMapping}
                setStateForPagnination={setStateForPagnination}
                functionCallBack={() => {}}
                tableName="Channels"
                notify={notify}
                showDownloadGridResults={false}
                showTotalCount={true}
                makePaginationStatic={false}
                hidePagination={getTotalTableRowCount() <= 25}
              />
            </Card.Body>
            <Card.Footer>
              <Row style={{ textAlign: "center" }}>
                <Col md="3">
                </Col>
                <Col md="3">
                  <Button
                    style={actionCustomButton}
                    variant="default"
                    className="btn-outline"
                    onClick={refreshPage}
                  >
                    Reset
                  </Button>
                </Col>
                <Col md="3">
                  <Button
                    style={actionCustomButton}
                    variant="primary"
                    disabled={!valueChanged}
                    onClick={() => {
                      buildParameters();
                      setShowSubmitModal(true);
                    }}
                  >
                    Save Changes
                  </Button>
                </Col>
                <Col md="3">
                </Col>
              </Row>
            </Card.Footer>
          </Card>
        </Col>
        <Modal
          toggle={() => setShowSubmitModal(!showSubmitModal)}
          size="lg"
          isOpen={showSubmitModal}
          scrollable={true}
        >
          <ModalHeader>
            <label>Entitlement Changes</label>
          </ModalHeader>
          <ModalBody>
            <Card className="card border-0">
              <Card.Body>
                <Table>
                  <thead>
                    <tr>
                      <th>Entitlement Type</th>
                      <th>Added</th>
                      <th>Removed</th>
                      <th>Pending Client Used</th>
                      <th>Client Allotment</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Monitoring</td>
                      <td>{channelReportingAdd}</td>
                      <td>{channelReportingRemove}</td>
                      <td>
                        {channelReportingAdd -
                          channelReportingRemove +
                          channelReportingUsed}
                      </td>
                      <td>{channelReportingAllotment}</td>
                    </tr>
                    <tr>
                      <td>Archiving</td>
                      <td>{channelArchivingAdd}</td>
                      <td>{channelArchivingRemove}</td>
                      <td>
                        {channelArchivingAdd -
                          channelArchivingRemove +
                          channelArchivingUsed}
                      </td>
                      <td>{channelArchivingAllotment} GB</td>
                    </tr>
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="secondary"
              onClick={() => {
                setShowSubmitModal(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                if (
                  channelReportingAdd -
                    channelReportingRemove +
                    channelReportingUsed >
                    channelReportingAllotment
                ) {
                  notify(
                    "danger",
                    "The selected change exceeds the allotment.  Please reduce your selection at or below the allotment."
                  );
                } else if (
                  channelReportingAdd == 0 &&
                  channelReportingRemove == 0 &&
                  channelArchivingAdd == 0 &&
                  channelArchivingRemove == 0
                ) {
                  notify("info", "No changes detected.");
                  setShowSubmitModal(false);
                } else {
                  handleSubmit();
                  setShowSubmitModal(false);
                }
              }}
            >
              Confirm
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </>
  );
}

export default EnvironmentEntitlements;
